import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const BEREICHE = ["Brand", "Digital", "Print"];
const KUNDE_MODELLE = [
  { id: "festpreis", label: "Festpreis" },
  { id: "stundenbasis", label: "Stundenbasis" },
  { id: "kontingent", label: "Stundenkontingent" },
  { id: "pauschale", label: "Monatspauschale" },
];
const DESIGNER_MODELLE = [
  { id: "stundenbasis", label: "Stundenbasis" },
  { id: "festpreis", label: "Festpreis" },
];

export default function ProjektNeu() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [kunden, setKunden] = useState([]);
  const [designer, setDesigner] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [f, setF] = useState({
    titel: "",
    kunde_id: "",
    designer_id: "",
    pm_id: "",
    bereich: "Digital",
    startdatum: new Date().toISOString().slice(0, 10),
    deadline: "",
    laufend: false,
    warnschwelle_prozent: 80,
    kundenmodell: "festpreis",
    kunden_betrag: "",
    kunden_kontingent_h: "",
    kunden_pauschale: "",
    kunden_stundensatz: "",
    kunden_stundensatz_bezeichnung: "",
    designermodell: "stundenbasis",
    designer_satz: "",
    designer_betrag: "",
    designer_kalk_stunden: "",
    hauptkontakt_id: "",
  });

  useEffect(() => {
    (async () => {
      const [k, d, u] = await Promise.all([api.get("/kunden"), api.get("/designer"), api.get("/users")]);
      setKunden(k.data);
      setDesigner(d.data.filter((x) => x.status === "approved"));
      setUsers(u.data.filter((x) => x.rolle === "pm" || x.rolle === "admin"));
    })();
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const num = (v) => (v === "" ? null : parseFloat(v));
      const payload = {
        titel: f.titel,
        kunde_id: f.kunde_id,
        designer_id: f.designer_id,
        pm_id: user.rolle === "admin" ? (f.pm_id || user.id) : user.id,
        bereich: f.bereich,
        startdatum: f.startdatum,
        deadline: f.laufend ? null : (f.deadline || null),
        laufend: f.laufend,
        warnschwelle_prozent: parseInt(f.warnschwelle_prozent, 10),
        kundenmodell: f.kundenmodell,
        kunden_betrag: f.kundenmodell === "festpreis" ? num(f.kunden_betrag) : null,
        kunden_kontingent_h: f.kundenmodell === "kontingent" ? num(f.kunden_kontingent_h) : null,
        kunden_pauschale: f.kundenmodell === "pauschale" ? num(f.kunden_pauschale) : null,
        kunden_stundensatz: (f.kundenmodell === "stundenbasis" || f.kundenmodell === "kontingent") ? num(f.kunden_stundensatz) : null,
        kunden_stundensatz_bezeichnung: (f.kundenmodell === "stundenbasis" || f.kundenmodell === "kontingent") ? (f.kunden_stundensatz_bezeichnung || null) : null,
        designermodell: f.designermodell,
        designer_satz: f.designermodell === "stundenbasis" ? num(f.designer_satz) : null,
        designer_betrag: f.designermodell === "festpreis" ? num(f.designer_betrag) : null,
        designer_kalk_stunden: num(f.designer_kalk_stunden),
        hauptkontakt_id: f.hauptkontakt_id || null,
      };
      const { data } = await api.post("/projekte", payload);
      nav(`/projekte/${data.id}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Fehler beim Anlegen");
    } finally { setBusy(false); }
  };

  const filteredDesigner = designer.filter((d) => (d.bereiche || []).includes(f.bereich));
  const selectedKunde = kunden.find((k) => k.id === f.kunde_id);
  const kundeSaetze = selectedKunde?.stundensaetze || [];

  const pickSatz = (idx) => {
    if (idx === "") { setF((s) => ({ ...s, kunden_stundensatz: "", kunden_stundensatz_bezeichnung: "" })); return; }
    if (idx === "custom") { setF((s) => ({ ...s, kunden_stundensatz_bezeichnung: "" })); return; }
    const s = kundeSaetze[parseInt(idx, 10)];
    if (s) setF((prev) => ({ ...prev, kunden_stundensatz: s.satz, kunden_stundensatz_bezeichnung: s.bezeichnung }));
  };

  return (
    <div>
      <Link to="/projekte" className="mono-label inline-flex items-center gap-1.5 hover:opacity-100 mb-4"><ArrowLeft size={12} /> Zurück</Link>

      <div className="mb-6">
        <div className="mono-label">Neu</div>
        <h1 className="font-archivo font-bold text-4xl mt-1">Projekt anlegen</h1>
      </div>

      <form onSubmit={submit} className="paper-card p-8 max-w-4xl space-y-6" data-testid="projekt-form">
        <section>
          <h2 className="mono-label mb-3">Basisdaten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="mono-label block mb-1">Projekttitel *</label>
              <input required value={f.titel} onChange={(e) => set("titel", e.target.value)} data-testid="p-titel"
                className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
            <div>
              <label className="mono-label block mb-1">Kunde *</label>
              <select required value={f.kunde_id} onChange={(e) => { set("kunde_id", e.target.value); set("hauptkontakt_id", ""); }} data-testid="p-kunde" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                <option value="">— wählen —</option>
                {kunden.map((k) => <option key={k.id} value={k.id}>{k.firmenname}</option>)}
              </select>
              {selectedKunde && (selectedKunde.ansprechpartner || []).length > 0 && (
                <div className="mt-2">
                  <label className="mono-label block mb-1">Hauptkontakt für Freigaben</label>
                  <select value={f.hauptkontakt_id} onChange={(e) => set("hauptkontakt_id", e.target.value)}
                    data-testid="p-hauptkontakt" className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm">
                    <option value="">— optional wählen —</option>
                    {(selectedKunde.ansprechpartner || []).map((a, i) => (
                      <option key={i} value={String(i)}>{a.name} {a.email ? `<${a.email}>` : ""}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div>
              <label className="mono-label block mb-1">Bereich *</label>
              <select value={f.bereich} onChange={(e) => set("bereich", e.target.value)} data-testid="p-bereich" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                {BEREICHE.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="mono-label block mb-1">Designer * ({filteredDesigner.length} verfügbar für {f.bereich})</label>
              <select required value={f.designer_id} onChange={(e) => set("designer_id", e.target.value)} data-testid="p-designer" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                <option value="">— wählen —</option>
                {filteredDesigner.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.stundensatz_standard} €/h</option>)}
              </select>
            </div>
            {user?.rolle === "admin" && (
              <div>
                <label className="mono-label block mb-1">PM</label>
                <select value={f.pm_id} onChange={(e) => set("pm_id", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                  <option value="">Ich selbst</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="mono-label mb-3">Zeitrahmen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mono-label block mb-1">Startdatum</label>
              <input type="date" value={f.startdatum} onChange={(e) => set("startdatum", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
            <div>
              <label className="mono-label block mb-1">Deadline</label>
              <input type="date" disabled={f.laufend} value={f.deadline} onChange={(e) => set("deadline", e.target.value)} data-testid="p-deadline" className="w-full px-3 py-2 border border-line rounded-sm bg-white disabled:bg-paper" />
            </div>
            <div className="flex items-end pb-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.laufend} onChange={(e) => set("laufend", e.target.checked)} data-testid="p-laufend" />
                Laufend
              </label>
            </div>
            <div>
              <label className="mono-label block mb-1">Warnschwelle</label>
              <div className="flex items-center gap-2">
                <input type="number" min="1" max="150" value={f.warnschwelle_prozent} onChange={(e) => set("warnschwelle_prozent", e.target.value)} className="w-24 px-3 py-2 border border-line rounded-sm bg-white" />
                <span className="mono-label" style={{opacity:1}}>%</span>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mono-label mb-3">Kundenkondition</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="mono-label block mb-1">Modell</label>
              <select value={f.kundenmodell} onChange={(e) => set("kundenmodell", e.target.value)} data-testid="p-kundenmodell" className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                {KUNDE_MODELLE.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div>
              {f.kundenmodell === "festpreis" && (
                <><label className="mono-label block mb-1">Betrag (€)</label><input type="number" step="0.01" value={f.kunden_betrag} onChange={(e) => set("kunden_betrag", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" /></>
              )}
              {f.kundenmodell === "kontingent" && (
                <><label className="mono-label block mb-1">Stunden pro Monat</label><input type="number" step="0.5" value={f.kunden_kontingent_h} onChange={(e) => set("kunden_kontingent_h", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" /></>
              )}
              {f.kundenmodell === "pauschale" && (
                <><label className="mono-label block mb-1">Monatspauschale (€)</label><input type="number" step="0.01" value={f.kunden_pauschale} onChange={(e) => set("kunden_pauschale", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" /></>
              )}
            </div>

            {(f.kundenmodell === "stundenbasis" || f.kundenmodell === "kontingent") && (
              <div className="md:col-span-2 border-t border-line pt-4 mt-2">
                <label className="mono-label block mb-1.5">Kundenstundensatz</label>
                {kundeSaetze.length > 0 ? (
                  <>
                    <select onChange={(e) => pickSatz(e.target.value)} data-testid="p-kundensatz-picker" className="w-full px-3 py-2 border border-line rounded-sm bg-white mb-2">
                      <option value="">— Satz wählen —</option>
                      {kundeSaetze.map((s, i) => <option key={i} value={i}>{s.bezeichnung} · {s.satz.toFixed(2)} €/h</option>)}
                      <option value="custom">Freier Satz…</option>
                    </select>
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="Bezeichnung" value={f.kunden_stundensatz_bezeichnung} onChange={(e) => set("kunden_stundensatz_bezeichnung", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm bg-white" />
                      <div className="flex items-center gap-2">
                        <input type="number" step="0.5" placeholder="€/h" value={f.kunden_stundensatz} onChange={(e) => set("kunden_stundensatz", e.target.value)} data-testid="p-kundensatz-wert" className="w-full px-3 py-2 border border-line rounded-sm text-sm bg-white" />
                        <span className="mono-label" style={{opacity:1}}>€/h</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <input placeholder="Bezeichnung (z.B. Standard)" value={f.kunden_stundensatz_bezeichnung} onChange={(e) => set("kunden_stundensatz_bezeichnung", e.target.value)} className="px-3 py-2 border border-line rounded-sm text-sm bg-white" />
                    <div className="flex items-center gap-2">
                      <input type="number" step="0.5" placeholder="€/h" value={f.kunden_stundensatz} onChange={(e) => set("kunden_stundensatz", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm text-sm bg-white" />
                      <span className="mono-label" style={{opacity:1}}>€/h</span>
                    </div>
                  </div>
                )}
                {kundeSaetze.length === 0 && selectedKunde && (
                  <div className="mono-label mt-2" style={{ color: "#8A5A00", opacity: 1 }}>
                    Für diesen Kunden sind noch keine Sätze hinterlegt. Du kannst hier einen freien Satz eintragen oder auf der Kunden-Detailseite Sätze anlegen.
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="mono-label mb-3">Designerkondition</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mono-label block mb-1">Modell</label>
              <select value={f.designermodell} onChange={(e) => set("designermodell", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white">
                {DESIGNER_MODELLE.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            {f.designermodell === "stundenbasis" && (
              <div>
                <label className="mono-label block mb-1">Satz (€/h)</label>
                <input type="number" step="0.01" value={f.designer_satz} onChange={(e) => set("designer_satz", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
            )}
            {f.designermodell === "festpreis" && (
              <div>
                <label className="mono-label block mb-1">Betrag (€)</label>
                <input type="number" step="0.01" value={f.designer_betrag} onChange={(e) => set("designer_betrag", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
              </div>
            )}
            <div>
              <label className="mono-label block mb-1">Kalk. Stunden</label>
              <input type="number" step="0.5" value={f.designer_kalk_stunden} onChange={(e) => set("designer_kalk_stunden", e.target.value)} className="w-full px-3 py-2 border border-line rounded-sm bg-white" />
            </div>
          </div>
        </section>

        {err && <div className="text-sm text-brunnerMagenta px-3 py-2 bg-brunnerMagenta/10 rounded-sm">{err}</div>}

        <div className="flex justify-end gap-2 pt-4 border-t border-line">
          <Link to="/projekte" className="px-4 py-2 border border-line rounded-sm">Abbrechen</Link>
          <button type="submit" disabled={busy} data-testid="p-submit" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
            {busy ? "Anlegen…" : "Projekt anlegen"}
          </button>
        </div>
      </form>
    </div>
  );
}
